import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import CheckoutDialog from '../components/CheckoutDialog';

const Events = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const [events] = useState([
    {
      id: 'ossington-2025',
      name: 'Ossington 2025',
      date: new Date('2025-09-28'),
      location: 'Highland Farm, Ossington, Newark',
      status: 'full',
      memberPrice: 18.00,
      regularPrice: 23.00,
      earlyBirdDiscount: 3.00,
      earlyBirdUntil: new Date('2025-09-22'),
      description: 'Ride across bridleways linked by country lanes and rural villages.',
      distances: ['3 miles (Lead Rein)', '9 miles', '16 miles']
    },
    {
      id: 'summer-championship-2025',
      name: 'Summer Endurance Championship',
      date: new Date('2025-07-15'),
      location: 'Windsor Great Park',
      status: 'upcoming',
      memberPrice: 45.00,
      regularPrice: 60.00,
      earlyBirdDiscount: 10.00,
      earlyBirdUntil: new Date('2025-06-01'),
      description: 'Join us for the premier endurance event of the summer season.',
      distances: ['25km', '50km', '80km']
    },
    {
      id: 'autumn-trail-2025',
      name: 'Autumn Trail Challenge',
      date: new Date('2025-09-20'),
      location: 'New Forest',
      status: 'upcoming',
      memberPrice: 35.00,
      regularPrice: 50.00,
      earlyBirdDiscount: 8.00,
      earlyBirdUntil: new Date('2025-08-15'),
      description: 'Experience the beautiful autumn colors on our challenging trail routes.',
      distances: ['30km', '60km']
    },
    {
      id: 'spring-training-2025',
      name: 'Spring Training Ride',
      date: new Date('2025-03-10'),
      location: 'Exmoor National Park',
      status: 'completed',
      memberPrice: 25.00,
      regularPrice: 35.00,
      description: 'Perfect preparation ride for the season ahead.',
      distances: ['20km', '40km']
    }
  ]);

  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [selectedEventForCheckout, setSelectedEventForCheckout] = useState(null);

  const getStatusBadge = (status) => {
    const severity = status === 'upcoming' ? 'success' : 
                    status === 'completed' ? 'info' : 'warning';
    return <Badge value={status.toUpperCase()} severity={severity} />;
  };

  const getPrice = (event) => {
    const now = new Date();
    const isEarlyBird = event.earlyBirdUntil && now <= event.earlyBirdUntil;
    let price = event.memberPrice;
    
    if (isEarlyBird) {
      price -= event.earlyBirdDiscount;
    }
    
    return price;
  };

  const handleRegister = (event) => {
    if (event.status === 'full') {
      // Handle waitlist directly
      const email = prompt('Enter your email address to join the waitlist:');
      if (email) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `You have been added to the waitlist for ${event.name}. You will be notified when spaces become available.`,
          life: 5000
        });
      }
    } else {
      // Navigate to booking page for available events
      navigate(`/events/${event.id}/book`);
    }
  };

  const handleViewDetails = (event) => {
    if (event.status === 'full') {
      navigate(`/events/${event.id}/waitlist`);
    } else {
      navigate(`/events/${event.id}/book`);
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      {/* Header */}
      <div className="se-card mb-4">
        <div className="se-card-header">
          <h2>Events</h2>
        </div>
        <div className="se-card-content">
          <Message
            severity="info"
            text="Browse and register for upcoming events. Member discounts and early bird pricing are automatically applied."
            className="w-full"
          />
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid">
        {events.map((event) => (
          <div key={event.id} className="col-12 lg:col-6">
            <Card className="h-full">
              <div className="flex justify-content-between align-items-start mb-3">
                <h3 className="mt-0 mb-1">{event.name}</h3>
                {getStatusBadge(event.status)}
              </div>
              
              <div className="text-600 mb-3">
                <div className="flex align-items-center mb-1">
                  <i className="pi pi-calendar mr-2"></i>
                  {event.date.toLocaleDateString('en-GB')}
                </div>
                <div className="flex align-items-center mb-1">
                  <i className="pi pi-map-marker mr-2"></i>
                  {event.location}
                </div>
                <div className="flex align-items-center">
                  <i className="pi pi-flag mr-2"></i>
                  {event.distances.join(', ')}
                </div>
              </div>

              <p className="text-700 mb-3">{event.description}</p>

              {/* Pricing */}
              <div className="mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                  <span>Member Price:</span>
                  <span className="font-bold se-text-primary">£{event.memberPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-content-between align-items-center mb-2">
                  <span>Regular Price:</span>
                  <span className="text-600">£{event.regularPrice.toFixed(2)}</span>
                </div>
                {event.earlyBirdDiscount && new Date() <= event.earlyBirdUntil && (
                  <div className="flex justify-content-between align-items-center mb-2">
                    <span className="se-text-secondary">Early Bird Discount:</span>
                    <span className="font-bold se-text-secondary">-£{event.earlyBirdDiscount.toFixed(2)}</span>
                  </div>
                )}
                {event.earlyBirdUntil && new Date() <= event.earlyBirdUntil && (
                  <small className="se-text-secondary block">
                    Early bird pricing until {event.earlyBirdUntil.toLocaleDateString('en-GB')}
                  </small>
                )}
              </div>

              {/* Final Price */}
              <div className="border-top-1 border-200 pt-3 mb-3">
                <div className="flex justify-content-between align-items-center">
                  <span className="font-bold">Your Price:</span>
                  <span className="font-bold text-xl se-text-primary">£{getPrice(event).toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid gap-2">
                <div className="col-12">
                  <Button
                    label="View Details"
                    icon="pi pi-eye"
                    className="w-full p-button-outlined"
                    onClick={() => handleViewDetails(event)}
                  />
                </div>
                <div className="col-12">
                  {event.status === 'upcoming' || event.status === 'full' ? (
                    <Button
                      label={event.status === 'full' ? 'Join Waitlist' : 'Book Now'}
                      icon={event.status === 'full' ? 'pi pi-plus' : 'pi pi-shopping-cart'}
                      className={`w-full ${event.status === 'full' ? 'p-button-warning' : 'p-button-primary'}`}
                      onClick={() => handleRegister(event)}
                    />
                  ) : (
                    <Button
                      label="View Results"
                      icon="pi pi-chart-line"
                      className="w-full p-button-secondary"
                      onClick={() => {
                        toast.current?.show({
                          severity: 'info',
                          summary: 'Info',
                          detail: 'Viewing event results (prototype)',
                          life: 3000
                        });
                      }}
                    />
                  )}
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Checkout Dialog */}
      <CheckoutDialog
        visible={checkoutVisible}
        onHide={() => setCheckoutVisible(false)}
        item={selectedEventForCheckout}
        itemType="event"
      />
    </div>
  );
};

export default Events;